import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalculateIcon from "@mui/icons-material/Calculate";
import { toast } from "react-toastify";
import {
  handleSpringDataRequest,
  handleSpringDataMutationRequest,
} from "../../utils/apiUtils";

function parseSpringMessage(data) {
  if (data == null) return {};
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  if (typeof data !== "object") return {};
  if ("message" in data) {
    const msg = data.message;
    if (typeof msg === "string") {
      try {
        return JSON.parse(msg);
      } catch {
        return {};
      }
    }
    if (msg && typeof msg === "object") return msg;
  }
  return data;
}

const StatCard = ({ icon, label, value, hint }) => (
  <Card variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight={700}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Card>
);

export default function AiCreditsGlobal() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalCredits, setGlobalCredits] = useState("100");
  const [globalMonthlyCredits, setGlobalMonthlyCredits] = useState("0");
  const [globalMultiplier, setGlobalMultiplier] = useState("3");
  const [globalCreditUsd, setGlobalCreditUsd] = useState("0.01");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await handleSpringDataRequest("core/api/v1/ai-credit/global-policy");
      const global = parseSpringMessage(res);
      if (global.default_subscription_credits != null) {
        setGlobalCredits(String(global.default_subscription_credits));
      }
      if (global.default_monthly_credits != null) {
        setGlobalMonthlyCredits(String(global.default_monthly_credits));
      }
      if (global.default_credit_multiplier != null) {
        setGlobalMultiplier(String(global.default_credit_multiplier));
      }
      if (global.credit_usd_value != null) {
        setGlobalCreditUsd(String(global.credit_usd_value));
      }
    } catch (e) {
      toast.error("Failed to load global AI credit policy");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await handleSpringDataMutationRequest(
        "PUT",
        "core/api/v1/ai-credit/global-policy",
        {
          default_subscription_credits: parseFloat(globalCredits) || 100,
          default_monthly_credits: parseFloat(globalMonthlyCredits) || 0,
          default_credit_multiplier: parseFloat(globalMultiplier) || 3,
          credit_usd_value: parseFloat(globalCreditUsd) || 0.01,
        }
      );
      toast.success("Global AI credit policy saved");
      await load();
    } catch (e) {
      toast.error("Failed to save global policy");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const multiplierNum = parseFloat(globalMultiplier) || 0;
  const usdNum = parseFloat(globalCreditUsd) || 0;
  const buyExample = 30;
  const realExample = multiplierNum > 0 ? buyExample / multiplierNum : 0;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          AI Credits — Global Defaults
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Applies to all hotels. Three buckets: (1) one-time free, (2) monthly
        (resets on billing anniversary, no rollover), (3) purchased (never
        expires). Debit order: free → monthly → purchased.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<AutoAwesomeIcon fontSize="small" color="primary" />}
          label="New hotel free grant"
          value={`${globalCredits} credits`}
          hint="One-time, given on signup"
        />
        <StatCard
          icon={<CalculateIcon fontSize="small" color="primary" />}
          label="1 credit costs"
          value={`$${usdNum.toFixed(4)}`}
          hint="Prepaid USD value per credit"
        />
        <StatCard
          icon={<CalculateIcon fontSize="small" color="primary" />}
          label="Example markup"
          value={`$${buyExample} → $${realExample.toFixed(2)} real`}
          hint={`At ${multiplierNum}× multiplier`}
        />
      </Stack>

      <Card variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Defaults for new / unconfigured hotels
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="One-time free credits"
              type="number"
              value={globalCredits}
              onChange={(e) => setGlobalCredits(e.target.value)}
              helperText="Granted once, on hotel signup"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Monthly credits"
              type="number"
              value={globalMonthlyCredits}
              onChange={(e) => setGlobalMonthlyCredits(e.target.value)}
              helperText="New hotels' monthly allowance"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Credit multiplier"
              type="number"
              inputProps={{ step: "0.1" }}
              value={globalMultiplier}
              onChange={(e) => setGlobalMultiplier(e.target.value)}
              helperText="e.g. 3 = $30 buy → $10 real"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Credit USD value"
              type="number"
              inputProps={{ step: "0.0001" }}
              value={globalCreditUsd}
              onChange={(e) => setGlobalCreditUsd(e.target.value)}
              helperText="1 credit = this many USD"
            />
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={saveGlobal}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Saving…" : "Save global defaults"}
          </Button>
        </Stack>
      </Card>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
        <Chip size="small" label="Per-hotel overrides live on each hotel's AI Credits tab" />
      </Stack>
    </Box>
  );
}
